import { ClientSoftwareTag } from '@kcftech/prometheus-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectInlineEditor } from 'components/inline_edit/MultiSelectInlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { queryClient } from 'utils/query';
import { joinNodes } from 'utils/joinNodes';

const CLIENT_SOFTWARE_TAG_OPTIONS: MultiSelectOption<ClientSoftwareTag>[] = Object.values(
    ClientSoftwareTag,
).map((tag) => ({
    title: tag,
    value: tag,
}));

const clientSoftwareTagsQueryKey = 'client-software-tags';

export type ClientSoftwareTagsCellProps = Readonly<{
    clientSoftwareId: number;
}>;

export function ClientSoftwareTagsCell({
    clientSoftwareId,
}: ClientSoftwareTagsCellProps) {
    const { showToast } = useNotificationContext();

    const { data: currentTags } = useQuery({
        queryKey: [clientSoftwareTagsQueryKey, clientSoftwareId],
        queryFn: () => sdClient.getClientSoftwareTags({ clientSoftwareId }),
    });

    const currentTagSet = new Set(currentTags ?? []);

    const { mutateAsync: syncTags, isPending } = useMutation({
        meta: { name: 'sync-client-software-tags' },
        mutationFn: async (selectedTags: ClientSoftwareTag[]) => {
            const selectedSet = new Set(selectedTags);
            const toAssign = selectedTags.filter((t) => !currentTagSet.has(t));
            const toRemove = (currentTags ?? []).filter((t) => !selectedSet.has(t));

            await Promise.all([
                ...toAssign.map((tag) =>
                    sdClient.assignClientSoftwareTag({ clientSoftwareId, tag }),
                ),
                ...toRemove.map((tag) =>
                    sdClient.removeClientSoftwareTag({ clientSoftwareId, tag }),
                ),
            ]);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [clientSoftwareTagsQueryKey, clientSoftwareId],
            });
            showToast({
                type: 'success',
                content: 'Successfully updated client software tags.',
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update client software tags.',
            }),
    });

    return (
        <MultiSelectInlineEditor
            value={CLIENT_SOFTWARE_TAG_OPTIONS.filter((opt) =>
                currentTagSet.has(opt.value),
            )}
            multiSelectConfig={{ options: CLIENT_SOFTWARE_TAG_OPTIONS }}
            renderValue={(tags) =>
                tags?.length ? (
                    joinNodes(
                        tags.map((t) => <code key={t.value}>{t.value}</code>),
                        ', ',
                    )
                ) : (
                    <NoValue />
                )
            }
            onConfirm={async (selected) => {
                if (selected !== undefined) {
                    await syncTags(selected.map((opt) => opt.value as ClientSoftwareTag));
                }
            }}
            isDisabled={isPending || !currentTags}
        />
    );
}
